import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import MessagesView from './MessagesView';
import PMTeamDashboard from './PMTeamDashboard';
import ProjectsView from './ProjectsView';
import TaskBoard from './TaskBoard';

const ProjectManagerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser } = useAuth();
  const { projects, tasks, users, attendance, getMyTeamData, checkIn, checkOut, loading } = useData();

  // Enhanced menu items with team analytics
  const pmMenuItems = [
    { name: 'Overview', path: '/dashboard', icon: 'home' },
    { name: 'My Projects', path: '/dashboard/projects', icon: 'briefcase' },
    { name: 'Tasks', path: '/dashboard/tasks', icon: 'check-square' },
    { name: 'Team Analytics', path: '/dashboard/team-analytics', icon: 'chart-line' },
    { name: 'Team Attendance', path: '/dashboard/team-attendance', icon: 'calendar-check' },
    { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' },
  ];

  // Enhanced project filtering for both Owner and PM roles
  const getCurrentUserId = () => {
    return currentUser?.id || currentUser?.uid;
  };

  const userId = getCurrentUserId();
  const userRole = currentUser?.role;

  // Get projects based on role
  let myProjects = [];
  if (userRole === 'owner') {
    myProjects = projects; // Owner sees all projects
  } else if (userRole === 'project_manager') {
    myProjects = projects.filter(p => p.createdBy === userId || p.managerId === userId);
  }

  const myTasks = tasks.filter(t => t.createdBy === userId);

  // Enhanced team data using the new function
  const { teamMembers, teamAttendance, teamTasks } = getMyTeamData();

  // Calculate enhanced stats
  const stats = {
    myProjects: myProjects.length,
    myTasks: myTasks.length,
    teamMembers: teamMembers.length,
    completedTasks: myTasks.filter(t => t.status === 'completed').length,
    teamTasksTotal: teamTasks.length,
    teamTasksCompleted: teamTasks.filter(t => t.status === 'completed').length,
    overdueTasks: teamTasks.filter(t => {
      const today = new Date();
      const dueDate = new Date(t.dueDate);
      return dueDate < today && t.status !== 'completed';
    }).length,
    presentToday: (() => {
      const todayDate = new Date().toISOString().split('T')[0];
      const todayAttendance = teamAttendance.filter(record => record.date === todayDate);
      return todayAttendance.filter(record => record.checkIn).length;
    })()
  };

  const DashboardHome = () => {
    // ADDED: Check-in/Check-out functionality
    const [todayAttendance, setTodayAttendance] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentWorkHours, setCurrentWorkHours] = useState(0);

    useEffect(() => {
      const todayDate = new Date().toISOString().split('T')[0];
      const myTodayRecord = attendance.find(record => 
        record.userId === userId && record.date === todayDate
      );
      setTodayAttendance(myTodayRecord);

      // Update current time every second
      const timer = setInterval(() => {
        setCurrentTime(new Date());
        
        // Calculate current work hours if checked in but not out
        if (myTodayRecord?.checkIn && !myTodayRecord?.checkOut) {
          const checkInTime = new Date(`${todayDate} ${myTodayRecord.checkIn}`);
          const now = new Date();
          const diffInMs = now - checkInTime;
          const diffInHours = diffInMs / (1000 * 60 * 60);
          setCurrentWorkHours(Math.max(0, diffInHours));
        }
      }, 1000);

      return () => clearInterval(timer);
    }, [attendance, userId]);

    // Handle check in
    const handleCheckIn = async () => {
      if (!todayAttendance?.checkIn && !loading) {
        try {
          await checkIn();
        } catch (error) {
          console.error('Check-in failed:', error);
        }
      }
    };

    // Handle check out
    const handleCheckOut = async () => {
      if (todayAttendance?.checkIn && !todayAttendance?.checkOut && !loading) {
        try {
          await checkOut();
        } catch (error) {
          console.error('Check-out failed:', error);
        }
      }
    };

    return (
      <div className="dashboard-content">
        <div className="dashboard-headerr">
          <h2>Welcome back, {currentUser.name}!</h2>
          <p>
            {userRole === 'owner' 
              ? 'Monitor all projects and teams across your organization.'
              : 'Manage your projects and team efficiently.'}
          </p>
        </div>

        {/* ADDED: Check-in/Check-out Section for Project Managers */}
        {/* UPDATED: Compact Check-in/Check-out Section for Project Managers */}
{userRole === 'project_manager' && (
  <div className="attendance-section">
    <div className="attendance-card-compact">
      <div className="attendance-header-compact">
        <h3>Today's Attendance</h3>
        <div className="current-time">
          {currentTime.toLocaleDateString()} - {currentTime.toLocaleTimeString()}
        </div>
      </div>
      
      <div className="attendance-content-compact">
        {/* Status Items in Row */}
        <div className="status-row">
          <div className="status-item-compact">
            <div className="status-icon-small check-in">
              <i className="fas fa-sign-in-alt"></i>
            </div>
            <div className="status-info-compact">
              <span className="status-label-small">Check In</span>
              <span className="status-time-small">
                {todayAttendance?.checkIn ? todayAttendance.checkIn.substring(0, 5) : '--:--'}
              </span>
            </div>
          </div>

          <div className="status-item-compact">
            <div className="status-icon-small check-out">
              <i className="fas fa-sign-out-alt"></i>
            </div>
            <div className="status-info-compact">
              <span className="status-label-small">Check Out</span>
              <span className="status-time-small">
                {todayAttendance?.checkOut ? todayAttendance.checkOut.substring(0, 5) : '--:--'}
              </span>
            </div>
          </div>

          <div className="status-item-compact">
            <div className="status-icon-small hours">
              <i className="fas fa-clock"></i>
            </div>
            <div className="status-info-compact">
              <span className="status-label-small">Work Hours</span>
              <span className="status-time-small">
                {todayAttendance?.checkOut ? 
                  `${todayAttendance.hoursWorked?.toFixed(1) || 0}h` : 
                  (todayAttendance?.checkIn ? `${currentWorkHours.toFixed(1)}h` : '0h')
                }
              </span>
            </div>
          </div>

          {/* Action Buttons in Same Row */}
          <div className="action-buttons-row">
            <button
              className="btn-compact btn-success-compact"
              onClick={handleCheckIn}
              disabled={loading || todayAttendance?.checkIn}
            >
              {loading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : todayAttendance?.checkIn ? (
                <i className="fas fa-check"></i>
              ) : (
                <i className="fas fa-sign-in-alt"></i>
              )}
              {todayAttendance?.checkIn ? 'Checked In' : 'Check In'}
            </button>

            <button
              className="btn-compact btn-warning-compact"
              onClick={handleCheckOut}
              disabled={loading || !todayAttendance?.checkIn || todayAttendance?.checkOut}
            >
              {loading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : todayAttendance?.checkOut ? (
                <i className="fas fa-check"></i>
              ) : !todayAttendance?.checkIn ? (
                <i className="fas fa-times"></i>
              ) : (
                <i className="fas fa-sign-out-alt"></i>
              )}
              {todayAttendance?.checkOut ? 'Checked Out' : 
               !todayAttendance?.checkIn ? 'Check In First' : 'Check Out'}
            </button>
          </div>
        </div>

        {/* Live Timer (only if checked in but not out) */}
        {todayAttendance?.checkIn && !todayAttendance?.checkOut && (
          <div className="work-timer-compact">
            <i className="fas fa-stopwatch"></i>
            <span>Working: {Math.floor(currentWorkHours)}h {Math.floor((currentWorkHours % 1) * 60)}m</span>
          </div>
        )}
      </div>
    </div>
  </div>
)}


        {/* Enhanced Stats Grid */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
              <i className="fas fa-briefcase"></i>
            </div>
            <div className="stat-info">
              <h3 className="text-primary">{stats.myProjects}</h3>
              <p className="text-secondary">
                {userRole === 'owner' ? 'Total Projects' : 'My Projects'}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-info">
              <h3 className="text-primary">{stats.teamMembers}</h3>
              <p className="text-secondary">
                {userRole === 'owner' ? 'All Employees' : 'Team Members'}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <i className="fas fa-tasks"></i>
            </div>
            <div className="stat-info">
              <h3 className="text-primary">{stats.teamTasksCompleted}/{stats.teamTasksTotal}</h3>
              <p className="text-secondary">Team Tasks Done</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-info">
              <h3 className="text-primary">{stats.overdueTasks}</h3>
              <p className="text-secondary">Overdue Tasks</p>
            </div>
          </div>
        </div>

        {/* Rest of your existing content remains the same */}
        {/* New Team Management Action Cards */}
        <div className="enhanced-action-cards">
          <h3>Team Management</h3>
          <div className="action-cards-grid">
            <div className="action-card" onClick={() => window.location.href = '/dashboard/team-analytics'}>
              <div className="action-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="action-content">
                <h4 className="text-primary">Team Analytics</h4>
                <p className="text-secondary">
                  {userRole === 'owner' 
                    ? 'View comprehensive analytics for all teams and projects'
                    : 'View detailed team performance metrics and trends'}
                </p>
              </div>
              <div className="action-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>

            <div className="action-card" onClick={() => window.location.href = '/dashboard/team-attendance'}>
              <div className="action-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="action-content">
                <h4 className="text-primary">Team Attendance</h4>
                <p className="text-secondary">
                  {userRole === 'owner'
                    ? 'Monitor attendance across all teams and departments'
                    : 'Monitor your team\'s attendance and working hours'}
                </p>
              </div>
              <div className="action-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>

            <div className="action-card" onClick={() => window.location.href = '/dashboard/tasks'}>
              <div className="action-icon">
                <i className="fas fa-tasks"></i>
              </div>
              <div className="action-content">
                <h4 className="text-primary">Task Management</h4>
                <p className="text-secondary">
                  {userRole === 'owner'
                    ? 'Oversee all tasks across projects and teams'
                    : 'Manage team tasks and track progress'}
                </p>
              </div>
              <div className="action-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Team Overview */}
        <div className="dashboard-card">
          <h3>Today's Team Overview</h3>
          <div className="today-team-stats">
            <div className="team-stat">
              <i className="fas fa-user-check"></i>
              <div>
                <span className="stat-number text-primary">{stats.presentToday}</span>
                <span className="stat-label text-secondary">Present Today</span>
              </div>
            </div>
            <div className="team-stat">
              <i className="fas fa-tasks"></i>
              <div>
                <span className="stat-number text-primary">{teamTasks.filter(t => t.status === 'ongoing').length}</span>
                <span className="stat-label text-secondary">Active Tasks</span>
              </div>
            </div>
            <div className="team-stat">
              <i className="fas fa-percentage"></i>
              <div>
                <span className="stat-number text-primary">
                  {stats.teamMembers > 0 ? Math.round((stats.presentToday / stats.teamMembers) * 100) : 0}%
                </span>
                <span className="stat-label text-secondary">Attendance Rate</span>
              </div>
            </div>
            <div className="team-stat">
              <i className="fas fa-chart-line"></i>
              <div>
                <span className="stat-number text-primary">
                  {stats.teamTasksTotal > 0 ? Math.round((stats.teamTasksCompleted / stats.teamTasksTotal) * 100) : 0}%
                </span>
                <span className="stat-label text-secondary">Completion Rate</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Dashboard Grid */}
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Active Projects</h3>
            <div className="project-list">
              {myProjects.filter(p => p.status === 'active').length > 0 ? (
                myProjects.filter(p => p.status === 'active').map(project => {
                  const projectTasks = teamTasks.filter(t => t.projectId === project.id);
                  const completedTasks = projectTasks.filter(t => t.status === 'completed');
                  const progress = projectTasks.length > 0 ? Math.round((completedTasks.length / projectTasks.length) * 100) : (project.progress || 0);
                  
                  return (
                    <div key={project.id} className="project-itemm">
                      <div className="project-info">
                        <h4 className="text-primary">{project.name}</h4>
                        <p className="text-secondary">{project.description}</p>
                        <div className="project-meta">
                          <span className="text-muted">
                            <i className="fas fa-users"></i>
                            Team: {project.teamMembers?.length || 0} members
                          </span>
                          <span className="text-muted">
                            <i className="fas fa-tasks"></i>
                            Tasks: {projectTasks.length}
                          </span>
                        </div>
                      </div>
                      <div className="project-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-primary">{progress}%</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">
                  <i className="fas fa-project-diagram"></i>
                  <p className="text-secondary">No active projects</p>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Team Members Status</h3>
            <div className="team-members-overview">
              {teamMembers.slice(0, 5).length > 0 ? (
                teamMembers.slice(0, 5).map(member => {
                  // Check today's attendance
                  const todayDate = new Date().toISOString().split('T')[0];
                  const todayAttendance = teamAttendance.find(
                    record => record.userId === member.id && record.date === todayDate
                  );

                  // Get member tasks
                  const memberTasks = teamTasks.filter(t => t.assigneeId === member.id);
                  const completedTasks = memberTasks.filter(t => t.status === 'completed');
                  const completionRate = memberTasks.length > 0 
                    ? Math.round((completedTasks.length / memberTasks.length) * 100) 
                    : 0;

                  return (
                    <div key={member.id} className="team-member-overview-item">
                      <div className="member-overview-info">
                        <div className="member-avatar">
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="text-primary">{member.name}</h4>
                          <span className="text-secondary">{member.role.replace('_', ' ')}</span>
                          <div className="member-status">
                            {todayAttendance?.checkIn ? (
                              <span className="status-present">
                                <i className="fas fa-check-circle"></i>
                                Present ({todayAttendance.hoursWorked || 0}h)
                              </span>
                            ) : (
                              <span className="status-absent">
                                <i className="fas fa-times-circle"></i>
                                Not checked in
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="member-performance">
                        <span className="performance-rate text-primary">{completionRate}%</span>
                        <small className="text-muted">completion</small>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">
                  <i className="fas fa-users"></i>
                  <p className="text-secondary">No team members assigned</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Original Task List (keeping your existing functionality) */}
        <div className="dashboard-card">
          <h3>Recent Tasks</h3>
          <div className="task-list">
            {myTasks.slice(0, 5).length > 0 ? (
              myTasks.slice(0, 5).map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-info">
                    <h4 className="lightindarktheme">{task.title}</h4>
                    <p className="lightindarktheme">Assigned to: {task.assigneeName}</p>
                    <span className="text-muted">Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                  </div>
                  <span className={`status-badge status-${task.status}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                </div>
              ))
            ) : (
              <div className="no-data">
                <i className="fas fa-tasks"></i>
                <p className="text-secondary">No tasks created yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={pmMenuItems}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title={`${userRole === 'owner' ? 'Organization' : 'Project Manager'} Dashboard`}
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/projects" element={<ProjectsView />} />
            <Route path="/tasks" element={<TaskBoard />} />
            <Route path="/team-analytics" element={<PMTeamDashboard />} />
            <Route path="/messages" element={<MessagesView />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ProjectManagerDashboard;
